/**
 * Collecting the five bid outcomes from a Prebid auction (Atlas ch.14).
 *
 * `packages/tag` subscribed only to `bidWon`, so losing bids, no-bids and
 * timeouts were captured nowhere. That is the gap this closes, and it matters
 * because **losing bids die in the browser**: no GAM report will ever contain
 * them, so a bid not captured here does not exist anywhere and cannot be
 * reconstructed later.
 *
 * Pure state machine over Prebid's events — no network, no globals — so every
 * transition below is testable without a browser.
 */

/** @typedef {"won"|"lost_gam"|"lost_prebid"|"no_bid"|"timeout"|"error"} BidOutcome */

const NEVER_SAMPLED = new Set(["won", "timeout", "error"]);

/** Prebid's bidResponse names the bidder `bidderCode`; its noBid and bidTimeout
 *  events carry bid REQUESTS, which name it `bidder`. */
const bidderOf = (b) => b?.bidderCode ?? b?.bidder;

/**
 * One auction on one slot.
 *
 * Prebid tells us who bid and who did not. It cannot tell us whether our winner
 * then lost inside GAM — only GPT's `slotRenderEnded` knows that — so the
 * collector starts a Prebid winner at `won` and downgrades to `lost_gam` when
 * the render says otherwise. Getting this backwards is how a publisher's "win
 * rate" ends up describing Prebid's auction rather than the one that pays.
 */
export class BidCollector {
  /**
   * @param {object} opts
   * @param {string} opts.slot       Playbid slot key.
   * @param {string} opts.requestId  Joins to the render beacon.
   * @param {string[]} opts.bidders  Every bidder ASKED. A bidder that never
   *   responds at all leaves no Prebid event, so without this list it would
   *   simply vanish rather than being recorded as a timeout.
   * @param {() => number} [opts.now]
   */
  constructor({ slot, requestId, bidders = [], now = () => Date.now() }) {
    this.slot = slot;
    this.requestId = requestId;
    this.now = now;
    this.startedAt = now();
    this.adUnitPath = undefined;
    this.shedBidders = [];
    /** @type {Map<string, {bidder: string, outcome: BidOutcome, cpmMicros?: number, bucket?: string, size?: string, latencyMs?: number, errorCode?: string}>} */
    this.bids = new Map();
    for (const b of bidders) this.bids.set(b, { bidder: b, outcome: "timeout" });
  }

  /** A bidder answered with a price. Provisionally the best-priced one wins. */
  onBidResponse(bid) {
    if (!bid?.bidderCode) return;
    const cpmMicros = Math.round((Number(bid.cpm) || 0) * 1_000_000);
    this.bids.set(bid.bidderCode, {
      bidder: bid.bidderCode,
      outcome: "lost_prebid",
      cpmMicros,
      bucket: bid.pbCg ?? bid.pbAg ?? undefined,
      size: bid.size ?? (bid.width && bid.height ? `${bid.width}x${bid.height}` : undefined),
      latencyMs: Number(bid.timeToRespond) || undefined,
    });
  }

  /** A bidder answered in time and declined. Distinct from a timeout on purpose:
   *  the two are identical in any revenue report and call for opposite responses. */
  onNoBid(bid) {
    const bidder = bidderOf(bid);
    if (!bidder) return;
    this.bids.set(bidder, {
      bidder,
      outcome: "no_bid",
      latencyMs: Number(bid.timeToRespond) || undefined,
    });
  }

  /** Prebid reports timeouts as a batch at the deadline. */
  onBidTimeout(list) {
    for (const b of list ?? []) {
      const bidder = bidderOf(b);
      if (!bidder) continue;
      const cur = this.bids.get(bidder);
      // A bidder that already answered is not a timeout, whatever Prebid says
      // about the batch — a late duplicate must not erase a real price.
      if (cur && cur.outcome !== "timeout") continue;
      this.bids.set(bidder, {
        bidder,
        outcome: "timeout",
        latencyMs: Number(b.timeToRespond) || undefined,
      });
    }
  }

  /** An adapter failed. Never sampled — errors are the diagnostic payload. */
  onBidderError(bidderCode, errorCode) {
    if (!bidderCode) return;
    this.bids.set(bidderCode, { bidder: bidderCode, outcome: "error", errorCode });
  }

  /** Prebid's winner. Provisional: GAM has not decided yet. */
  onBidWon(bid) {
    if (!bid?.bidderCode) return;
    const cur = this.bids.get(bid.bidderCode) ?? { bidder: bid.bidderCode, outcome: "lost_prebid" };
    this.bids.set(bid.bidderCode, { ...cur, outcome: "won" });
  }

  /**
   * GPT has rendered (or collapsed) this slot. GAM has now decided, so the
   * outcome of every bid is knowable — but the Prebid winner is only named by
   * `bidWon`, which the Prebid creative fires just AFTER this event. The final
   * labels are therefore settled in `finalize()`, not here.
   *
   * Kept tolerant of the older call shape (`prebidLineItem`): a caller that
   * says our line item did NOT serve demotes an early `won` straight away.
   *
   * @param {object} e
   * @param {boolean} e.isEmpty
   * @param {string|number|null} [e.lineItemId] null on an AdX win.
   * @param {string} [e.adUnitPath]
   * @param {boolean} [e.prebidLineItem]
   */
  onSlotRenderEnded(e) {
    this.adUnitPath = e?.adUnitPath ?? this.adUnitPath;
    this.rendered = true;
    this.renderedEmpty = e?.isEmpty === true;
    if (e?.prebidLineItem === true && e?.isEmpty !== true) return;
    if (e?.prebidLineItem === false || e?.isEmpty === true) {
      for (const [k, v] of this.bids) {
        if (v.outcome === "won") this.bids.set(k, { ...v, outcome: "lost_gam" });
      }
    }
  }

  /**
   * Settle the labels once GAM and Prebid have both spoken.
   *
   * Under send-all-bids every bid with a price reaches GAM as its own key, so
   * GAM — not Prebid — picks among them: once GAM has answered, every priced
   * bid that did not win lost inside GAM (`lost_gam`), whether AdX, direct or
   * another bidder's line item beat it, or nothing matched at all. The only
   * `lost_prebid` left is a bid whose keys were shed before the request (it
   * never reached GAM). Without a GAM answer nothing is relabelled.
   */
  finalize() {
    if (!this.rendered) return;
    const shed = new Set(this.shedBidders ?? []);
    for (const [k, v] of this.bids) {
      if (v.outcome !== "lost_prebid") continue;
      if (shed.has(k)) continue;
      this.bids.set(k, { ...v, outcome: "lost_gam" });
    }
  }

  onKeyValuesShed(bidders) {
    this.shedBidders = [...new Set([...(this.shedBidders ?? []), ...(bidders ?? [])])];
  }

  /**
   * The batch to POST. Applies client-side sampling, never to the outcomes that
   * anchor the join to revenue or carry the diagnosis.
   *
   * @param {object} [opts]
   * @param {number} [opts.samplingRate] 0..1 from the server-side config.
   * @param {() => number} [opts.rnd]
   */
  toBatch({ samplingRate = 1, rnd = Math.random } = {}) {
    const rate = Number.isFinite(samplingRate) && samplingRate > 0 && samplingRate <= 1 ? samplingRate : 1;
    const sampled = new Set();
    const bids = [];
    for (const b of this.bids.values()) {
      if (rate < 1 && !NEVER_SAMPLED.has(b.outcome)) {
        // Declared when the outcome is SUBJECT to sampling, not when a record
        // happens to survive it. Otherwise a batch that drops every no_bid
        // reports no rate at all, and the server has no way to know the ones it
        // does receive later stand for ten each — the counts would silently
        // disagree between batches.
        sampled.add(b.outcome);
        if (rnd() > rate) continue;
      }
      bids.push(b);
    }
    return {
      requestId: this.requestId,
      slot: this.slot,
      adUnitPath: this.adUnitPath,
      auctionMs: this.now() - this.startedAt,
      bids,
      ...(sampled.size > 0 ? { sampling: { rate, sampled: [...sampled] } } : {}),
      ...(this.shedBidders.length > 0 ? { shedBidders: this.shedBidders } : {}),
    };
  }
}
